import {
  HttpClient,
  HttpBackend,
  HttpHeaders,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';

import { User } from '../models/auth.models';
import { environment } from 'src/environments/environment';
import { PermissionService } from './permission.service';
import { NativeBridgeService } from './native-bridge.service';

const REFRESH_SKEW_SECONDS = 300;

const AUTO_REFRESH_INTERVAL_MS = 60_000;

@Injectable({ providedIn: 'root' })
export class AuthfakeauthenticationService {
  private currentUserSubject: BehaviorSubject<User>;
  public currentUser: Observable<User>;
  private httpNoInterceptor: HttpClient;

  private refreshInFlight: Observable<any> | null = null;

  private autoRefreshStarted = false;

  constructor(
    private http: HttpClient,
    private backend: HttpBackend,
    private permissionService: PermissionService,
    private nativeBridge: NativeBridgeService,
  ) {
    this.httpNoInterceptor = new HttpClient(backend);
    const storedUser = localStorage.getItem('currentUser');
    const user = storedUser ? JSON.parse(storedUser) : null;
    this.currentUserSubject = new BehaviorSubject<User>(user);
    this.currentUser = this.currentUserSubject.asObservable();

    if (user) {
      this.loadUserPermissions(user);
    }
  }

  private loadUserPermissions(user: any) {
    this.permissionService.setLoading(true);

    // Si los permisos ya vienen en el objeto de usuario (respuesta del login), usarlos directamente
    if (user.permissions && Array.isArray(user.permissions)) {
      //console.log('Permission Load - Using permissions from user object:', user.permissions);
      this.permissionService.setPermissions(user.permissions);
      return;
    }

    if (user.role === 'Admin' || user.role === 0) {
      this.permissionService.setPermissions([]);
      return;
    }

    const roleId = user.user?.roleId || user.user?.role || user.role;
    //console.log('Permission Load - Detected Role ID:', roleId, 'User object:', user);

    if (roleId) {
      // Using httpNoInterceptor to bypass JwtInterceptor and avoid circular dependency during bootstrap.
      // We manually add the Authorization header since JwtInterceptor is bypassed.
      const headers = new HttpHeaders({
        Authorization: `Bearer ${user.token}`,
      });

      this.httpNoInterceptor
        .get<any>(environment.url + '/PermissionAdmin/role/' + roleId, {
          headers,
        })
        .subscribe(
          (resp: any) => {
            const permissions = Array.isArray(resp)
              ? resp
              : resp.data || resp.permissions || [];
            // We include both code and name to match both use cases in the guards
            const permCodes = [];
            permissions.forEach((p) => {
              if (p.code) permCodes.push(p.code);
              if (p.name) permCodes.push(p.name);
            });
            this.permissionService.setPermissions(permCodes);
          },
          (error) => {
            console.error('Error loading permissions', error);
            this.permissionService.setLoading(false);
          },
        );
    } else {
      console.warn(
        'No Role ID found in user object. Permissions will be empty.',
      );
      this.permissionService.setPermissions([]);
    }
  }

  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  login(email: string, password: string) {
    return this.http
      .post<any>(
        environment.url + environment.Authenticate + environment.login,
        {
          email,
          password,
          deviceId: this.nativeBridge.deviceId,
          platform: this.nativeBridge.platform,
        },
      )
      .pipe(
        map((user) => {
          if (user && user.token) {
            this.persistSession(user);
            this.loadUserPermissions(user);
          }
          return user;
        }),
      );
  }

  socialLogin(provider: string, token: string) {
    return this.http
      .post<any>(
        environment.url + environment.Authenticate + environment.social,
        {
          provider,
          token,
          deviceId: this.nativeBridge.deviceId,
          platform: this.nativeBridge.platform,
        },
      )
      .pipe(
        map((user) => {
          if (user && user.token) {
            this.persistSession(user);
            this.loadUserPermissions(user);
          }
          return user;
        }),
      );
  }

  restoreSession(currentUser: any): void {
    if (!currentUser || !currentUser.refreshToken) return;

    localStorage.setItem('currentUser', JSON.stringify(currentUser));
    this.currentUserSubject.next(currentUser);
  }

  refreshSession(): Observable<any> {
    if (this.refreshInFlight) return this.refreshInFlight;

    const current: any = this.currentUserValue;
    const refreshToken = current?.refreshToken;

    if (!refreshToken) {
      return throwError(() => 'Sesión no disponible');
    }

    this.refreshInFlight = this.httpNoInterceptor
      .post<any>(
        environment.url + environment.Authenticate + environment.refresh,
        {
          refreshToken,
          deviceId: this.nativeBridge.deviceId,
          platform: this.nativeBridge.platform,
        },
      )
      .pipe(
        map((resp: any) => {
          if (!resp || !resp.token || !resp.success) {
            throw resp?.error || 'No fue posible renovar la sesión';
          }
          this.persistSession({ ...current, ...resp, user: resp.user ?? current.user });
          this.loadUserPermissions(this.currentUserValue);
          return resp;
        }),
        catchError((err) => {
          if (this.isRefreshRejected(err)) {
            this.logout();
          }
          return throwError(() => err);
        }),
        finalize(() => {
          this.refreshInFlight = null;
        }),
        shareReplay(1),
      );

    return this.refreshInFlight;
  }

  startAutoRefresh(): void {
    if (this.autoRefreshStarted) return;
    this.autoRefreshStarted = true;

    setInterval(() => this.refreshIfNeeded(), AUTO_REFRESH_INTERVAL_MS);

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.refreshIfNeeded();
    });
  }

  private refreshIfNeeded(): void {
    if (!this.hasRefreshToken() || !this.accessTokenNeedsRefresh()) return;

    this.refreshSession().subscribe({ error: () => {} });
  }

  private isRefreshRejected(err: any): boolean {
    if (err instanceof HttpErrorResponse) {
      return err.status === 400 || err.status === 401 || err.status === 403;
    }

    return true;
  }

  hasRefreshToken(): boolean {
    return !!(this.currentUserValue as any)?.refreshToken;
  }

  accessTokenNeedsRefresh(): boolean {
    const token = (this.currentUserValue as any)?.token;
    if (!token || token === 'undefined') return false;

    const expiration = this.tokenExpiration(token);
    if (expiration === null) return false;

    return Date.now() > expiration - REFRESH_SKEW_SECONDS * 1000;
  }

  tokenExpiration(token: string): number | null {
    try {
      const payloadBase64 = token.split('.')[1];
      if (!payloadBase64) return null;

      const payload = JSON.parse(atob(payloadBase64));
      return payload?.exp ? payload.exp * 1000 : null;
    } catch (e) {
      console.error('Error al decodificar el token', e);
      return null;
    }
  }

  logout() {
    const refreshToken = (this.currentUserValue as any)?.refreshToken;

    if (refreshToken) {
      this.httpNoInterceptor
        .post<any>(
          environment.url + environment.Authenticate + environment.logout,
          { refreshToken },
        )
        .subscribe({ error: () => {} });
    }

    localStorage.removeItem('currentUser');
    this.permissionService.clearPermissions();
    this.currentUserSubject.next(null);
    this.nativeBridge.clearSession();
  }

  private persistSession(user: any): void {
    localStorage.setItem('currentUser', JSON.stringify(user));
    this.currentUserSubject.next(user);
    this.nativeBridge.saveSession(user);
  }
}
